import { interpolate } from 'react-spring';
import { addV, subV } from 'react-use-gesture';
import { Transformation, Vector, ZoomSpring } from './useZoom.types';

export const addVector = addV;
export const subtractVector = subV;

const scaleVector = (vector: [number, number], scale: number): Vector => [
    vector[0] * scale,
    vector[1] * scale
];

// Combines the active and existing transformations into a single transformation
export const combineTransformations = (
    existing: Transformation,
    active: Transformation
): Transformation => {
    // Shift active translation so that subsequent calculations have an origin of [0 ,0]
    const activeOriginShifted: Vector = scaleVector(active.origin, -1);

    // Scale translation up
    const scaledActiveOrigin: Vector = scaleVector(
        activeOriginShifted,
        active.scale
    );

    // Shift translation back to its starting point
    const finalActiveOrigin = addVector(scaledActiveOrigin, active.origin);

    // Existing transformation needs to be scaled by the active scale amount
    const scaledExistingTranslation = scaleVector(
        existing.translation,
        active.scale
    );

    // Now we just need to add them up to calculate the merged transformation
    const combined: Transformation = {
        origin: [0, 0],
        translation: addVector(
            addVector(finalActiveOrigin, scaledExistingTranslation),
            active.translation
        ),
        scale: existing.scale * active.scale
    };

    return combined;
};

export const emptyTransformation = (): Transformation => ({
    origin: [0, 0],
    translation: [0, 0],
    scale: 1
});

export const interpolateSpringToCSSTransformValue = (
    transforms: ZoomSpring
) => {
    return interpolate(
        [
            // React spring typing is poorly implemented in v8
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            transforms.activeOrigin,
            transforms.activeTranslation as unknown as number,
            transforms.activeScale as unknown as number,
            transforms.existingTranslation as unknown as number,
            transforms.existingScale as unknown as number
        ],
        (
            origin: [number, number],
            translation: [number, number],
            scale: number,
            existingTranslation: [number, number],
            existingScale: number
        ) => {
            // Shift the active transformation so that the focal point is moved to [0,0] so it
            // becomes the origin of any subsequent transformations
            const translatedOrigin = [
                -origin[0] * scale + origin[0],
                -origin[1] * scale + origin[1]
            ];

            const activeTransformation = `translate(${translation[0]}px, ${translation[1]}px) translate(${translatedOrigin[0]}px, ${translatedOrigin[1]}px) scale(${scale})`;
            const existingTransformation = `translate(${existingTranslation[0]}px, ${existingTranslation[1]}px) scale(${existingScale})`;

            // Transformations are applied in reverse order
            return `${activeTransformation} ${existingTransformation}`;
        }
    );
};
